import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { invitesApi, type Invite } from "@/api/invites";
import { useConfirm } from "@/components/feedback/Confirm";
import { useToast } from "@/components/feedback/Toast";
import { Button } from "@/components/primitives/Button";
import { FloatingCard } from "@/components/primitives/FloatingCard";
import { useSession } from "@/session/SessionProvider";

export default function InvitesPage() {
	const { user, isLoading } = useSession();
	const { toast } = useToast();
	const confirm = useConfirm();
	const [invites, setInvites] = useState<Invite[]>([]);
	const [lastInvite, setLastInvite] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [showArchived, setShowArchived] = useState(false);

	const load = useCallback(async () => {
		try {
			const res = await invitesApi.list(showArchived);
			setInvites(res.invites);
			setError(null);
		} catch (e) {
			setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
		}
	}, [showArchived]);

	useEffect(() => {
		if (user?.isAdmin) void load();
	}, [load, user?.isAdmin]);

	if (isLoading) {
		return (
			<div className="flex min-h-[40vh] items-center justify-center text-sm text-fg-muted">
				Laden…
			</div>
		);
	}
	if (!user?.isAdmin) return <Navigate to="/app" replace />;

	const onCreate = async () => {
		setBusy(true);
		setError(null);
		try {
			const { invite } = await invitesApi.create(7);
			setLastInvite(invite.url);
			await load();
			try {
				await navigator.clipboard.writeText(invite.url);
				toast("Einladungslink erstellt und kopiert", "success");
			} catch {
				toast("Einladungslink erstellt", "success");
			}
		} catch (e) {
			setError(e instanceof Error ? e.message : "Erzeugen fehlgeschlagen");
		} finally {
			setBusy(false);
		}
	};

	const onArchive = async (id: string) => {
		const ok = await confirm({
			title: "Einladung archivieren?",
			description: "Der Link funktioniert danach nicht mehr.",
			confirmLabel: "Archivieren",
			variant: "danger",
		});
		if (!ok) return;
		try {
			await invitesApi.archive(id);
			await load();
			toast("Einladung archiviert", "success");
		} catch (e) {
			setError(e instanceof Error ? e.message : "Archivieren fehlgeschlagen");
		}
	};

	return (
		<div className="flex flex-1 justify-center px-4 py-8">
			<FloatingCard className="w-full max-w-lg p-5">
				<header>
					<p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
						Admin
					</p>
					<h1 className="mt-1 text-lg font-semibold tracking-tight">Einladungen</h1>
					<p className="mt-1 text-sm text-fg-muted">
						Registrierung ist nur per Einladung möglich. Nur Admins können Links erzeugen.
					</p>
				</header>

				{error ? <p className="mt-4 text-sm text-rose-500">{error}</p> : null}

				<div className="mt-5">
					{!showArchived ? (
						<>
							<Button size="sm" onClick={() => void onCreate()} disabled={busy}>
								{busy ? "…" : "Neuen Einladungslink"}
							</Button>
							{lastInvite ? (
								<div className="mt-3 flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] bg-surface-raised p-2 pl-3">
									<code className="min-w-0 flex-1 break-all font-mono text-xs text-teal-600 dark:text-teal-400">
										{lastInvite}
									</code>
									<Button
										variant="secondary"
										size="sm"
										onClick={() => {
											void navigator.clipboard.writeText(lastInvite);
											toast("Link kopiert", "success");
										}}
									>
										Kopieren
									</Button>
								</div>
							) : null}
						</>
					) : null}

					<div className={showArchived ? "" : "mt-5"}>
						<div className="mb-2 flex items-center justify-between">
							<p className="text-sm font-medium text-fg">
								{showArchived ? "Archiviert" : "Aktiv"}
							</p>
							<label className="flex cursor-pointer select-none items-center gap-1.5 text-xs text-fg-muted">
								<input
									type="checkbox"
									checked={showArchived}
									onChange={(e) => setShowArchived(e.target.checked)}
									className="accent-teal-500"
								/>
								Archiv anzeigen
							</label>
						</div>
						{invites.length === 0 ? (
							<p className="text-sm text-fg-muted">
								{showArchived
									? "Keine archivierten Einladungen."
									: "Noch keine Einladungen. Erzeuge oben einen Link."}
							</p>
						) : (
							<ul className="divide-y divide-zinc-950/[0.06] overflow-hidden rounded-[var(--radius-control)] bg-surface-raised dark:divide-white/[0.06]">
								{invites.map((invite) => (
									<li
										key={invite.id}
										className="flex flex-wrap items-center gap-3 px-3 py-2.5"
									>
										<div className="min-w-0 flex-1">
											<code className="break-all font-mono text-xs text-fg">
												{invite.url}
											</code>
											<p className="mt-0.5 text-xs text-fg-muted">
												{invite.useCount}× genutzt
												{invite.deletedAt
													? ` · archiviert ${invite.deletedAt}`
													: invite.expiresAt
														? ` · läuft ab ${invite.expiresAt}`
														: ""}
											</p>
										</div>
										<button
											type="button"
											className="text-xs text-teal-600 hover:underline dark:text-teal-400"
											onClick={() => {
												void navigator.clipboard.writeText(invite.url);
												toast("Link kopiert", "success");
											}}
										>
											Kopieren
										</button>
										{!showArchived ? (
											<button
												type="button"
												className="text-xs text-fg-muted hover:text-rose-500"
												onClick={() => void onArchive(invite.id)}
											>
												Archivieren
											</button>
										) : null}
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			</FloatingCard>
		</div>
	);
}
